/**
 * Типы и подписи вкладки «Уведомления».
 *
 * Чистые данные и функции: их проверяют без браузера. Состав уведомления и
 * текущее состояние заказа приходят с сервера; здесь только человеческие
 * подписи и раскладка.
 */

export interface CompositionDiff {
  added: { name: string; quantity: string }[];
  removed: { name: string; quantity: string }[];
  quantityChanged: { name: string; old: string; new: string }[];
  parameterChanged: { name: string }[];
}

/**
 * Полезная нагрузка уведомления.
 *
 * `fields`/`composition` есть ТОЛЬКО у уведомлений об изменении заказа. У других
 * типов (карантин «Нет цветов», эскалация, отказ) их нет вовсе — поэтому оба
 * поля необязательны, и читать их надо через {@link changeFields} /
 * {@link changeComposition}, а не напрямую: прямое `payload.fields.length`
 * роняло весь интерфейс на записи без этих полей.
 */
export interface NotificationPayload {
  fields?: { category: string; label: string; old: string | null; new: string | null }[];
  composition?: CompositionDiff | null;
}

/** `kind` уведомления карантина «Нет цветов»/«Нет товара» (совпадает с сервером). */
export const NO_FLOWERS_QUARANTINE_KIND = 'NO_FLOWERS_QUARANTINE';

/** Данные карантина «Нет цветов» в payload уведомления. Все поля необязательны. */
export interface NoFlowersNotificationData {
  floristName: string | null;
  reason: string | null;
  comment: string | null;
}

/** Безопасно читает данные карантина из payload неизвестной формы. */
export function noFlowersData(payload: unknown): NoFlowersNotificationData {
  const p = (payload ?? {}) as Record<string, unknown>;
  return {
    floristName: typeof p['floristName'] === 'string' ? p['floristName'] : null,
    reason: typeof p['reason'] === 'string' ? p['reason'] : null,
    comment: typeof p['comment'] === 'string' ? p['comment'] : null,
  };
}

/** Список изменённых полей заказа или пустой массив — безопасно к любой форме. */
export function changeFields(
  payload: NotificationPayload | undefined,
): { category: string; label: string; old: string | null; new: string | null }[] {
  return Array.isArray(payload?.fields) ? payload.fields : [];
}

/** Diff состава или `null` — безопасно к любой форме. */
export function changeComposition(
  payload: NotificationPayload | undefined,
): CompositionDiff | null {
  return payload?.composition ?? null;
}

export interface OrderStateView {
  kind:
    | 'UNASSIGNED'
    | 'WITH_FLORIST'
    | 'AWAITING_INTAKE'
    | 'IN_STORAGE_CELL'
    | 'IN_ROUTE_CELL'
    | 'IN_ROUTE'
    | 'WITH_COURIER'
    | 'DELIVERED'
    | 'CANCELLED'
    | 'WRITTEN_OFF';
  cellCode?: string;
  routeNumber?: string;
  routeState?: string;
  courierName?: string;
}

export interface NotificationView {
  id: string;
  orderId: string;
  orderNumber: string;
  occurredAt: string;
  source: string;
  categories: string[];
  kind: string;
  payload: NotificationPayload;
  read: boolean;
  currentState: OrderStateView;
  decision: {
    assignedFloristId: string;
    assignedFloristName: string;
    decidedByName: string;
    assemblyRound: number;
    decidedAt: string;
  } | null;
  /**
   * Запрос отказа флориста (kind `REFUSAL_REQUEST`). Отдельное событие со своим
   * набором решений: Отклонить / Подтвердить отказ / Передать другому.
   */
  refusal: {
    state: 'PENDING' | 'REJECTED' | 'APPROVED' | 'TRANSFERRED';
    reason: string;
    comment: string | null;
    floristId: string;
    floristName: string;
    decidedByName: string | null;
    decidedAt: string | null;
  } | null;
}

/** Причина отказа словами. Совпадает с серверным enum `OrderRefusalReason`. */
const REFUSAL_REASON_LABEL: Record<string, string> = {
  INSUFFICIENT_GOODS: 'Не хватает товара',
  CANNOT_ASSEMBLE: 'Не могу собрать',
  PHYSICALLY_IMPOSSIBLE: 'Физически невозможно',
  WRONG_ASSIGNMENT: 'Ошибочное назначение',
  OTHER: 'Другое',
};

export function refusalReasonLabel(reason: string): string {
  return REFUSAL_REASON_LABEL[reason] ?? reason;
}

/** Итог решения по отказу словами. `PENDING` — ещё ждёт руководителя. */
const REFUSAL_STATE_LABEL: Record<string, string> = {
  PENDING: 'Ожидает решения',
  REJECTED: 'Отклонён',
  APPROVED: 'Отказ подтверждён',
  TRANSFERRED: 'Передан другому',
};

export function refusalStateLabel(state: string): string {
  return REFUSAL_STATE_LABEL[state] ?? state;
}

/** `kind` уведомления-эскалации задачи логиста. */
export const LOGIST_TASK_ESCALATION_KIND = 'LOGIST_TASK_ESCALATION';

/** Вид задачи логиста словами (совпадает с серверным `OrderResolutionKind`). */
const RESOLUTION_KIND_LABEL: Record<string, string> = {
  FAILED_DELIVERY: 'недоставка',
  CANCELLED_AFTER_DELIVERY: 'отмена после доставки',
};

export function resolutionKindLabel(kind: string): string {
  return RESOLUTION_KIND_LABEL[kind] ?? kind;
}

export interface NotificationsResponse {
  items: NotificationView[];
  total: number;
  unread: number;
}

/** Источник изменения словами. */
export function sourceLabel(source: string): string {
  return source === 'MOYSKLAD_SYNC' ? 'МойСклад (синхронизация)' : source;
}

const ROUTE_STATE_LABEL: Record<string, string> = {
  DRAFT: 'черновик',
  CONFIRMED: 'подтверждён',
  ACTIVE: 'активен',
  COMPLETED: 'завершён',
  CANCELLED: 'отменён',
};

/**
 * Текущее состояние заказа словами. Всегда ясно, в маршрутном ли листе заказ и
 * в каком именно: номер листа входит в подпись везде, где он есть.
 */
export function orderStateLabel(state: OrderStateView): string {
  switch (state.kind) {
    case 'UNASSIGNED':
      return 'Не назначен в маршрутный лист';
    case 'WITH_FLORIST':
      return 'У флориста';
    case 'AWAITING_INTAKE':
      return 'Собран, ожидает приёмки';
    case 'IN_STORAGE_CELL':
      return `В ячейке хранения ${state.cellCode ?? ''}`.trim();
    case 'IN_ROUTE_CELL':
      return `В маршрутной ячейке ${state.cellCode ?? ''}${
        state.routeNumber === undefined ? '' : ` · МЛ ${state.routeNumber}`
      }`.trim();
    case 'IN_ROUTE':
      return `В маршрутном листе ${state.routeNumber ?? ''}${
        state.routeState === undefined
          ? ''
          : ` (${ROUTE_STATE_LABEL[state.routeState] ?? state.routeState})`
      }`.trim();
    case 'WITH_COURIER':
      return `У курьера${state.courierName === undefined ? '' : ` ${state.courierName}`} · МЛ ${
        state.routeNumber ?? ''
      }`.trim();
    case 'DELIVERED':
      return `Доставлен${state.routeNumber === undefined ? '' : ` · МЛ ${state.routeNumber}`}`;
    case 'CANCELLED':
      return 'Отменён';
    case 'WRITTEN_OFF':
      return 'Списан';
    default:
      return '—';
  }
}

/** Есть ли в diff состава хоть одно изменение. */
export function hasCompositionChange(diff: CompositionDiff | null): boolean {
  if (diff === null) {
    return false;
  }
  return (
    diff.added.length > 0 ||
    diff.removed.length > 0 ||
    diff.quantityChanged.length > 0 ||
    diff.parameterChanged.length > 0
  );
}
